import { lazy, Suspense, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router';
import Home from './routes/home';
import Layout from './components/layout/layout';
import { useAuth } from './context/auth-context';
import { AuthLoading } from './components/auth/auth-loading';
import { AccountRejectedNotice } from './components/auth/account-rejected-notice';
import { RequireOnboarded } from './components/auth/require-onboarded';
import { TooltipProvider } from './components/shadcn/tooltip';
import { trackPageview } from './lib/analytics';

// Auth/legal/admin surfaces are code-split so the cold load for `/` and
// `/:drug` doesn't pay for signup forms and the admin queue up front.
const Login = lazy(() => import('./routes/login'));
const SignUp = lazy(() => import('./routes/signup'));
const Invite = lazy(() => import('./routes/invite'));
const Onboarding = lazy(() => import('./routes/onboarding'));
const Terms = lazy(() => import('./routes/terms'));
const Privacy = lazy(() => import('./routes/privacy'));
const Documents = lazy(() => import('./routes/documents'));
const Feedback = lazy(() => import('./routes/feedback'));
const AdminDrugs = lazy(() => import('./routes/admin-drugs'));
const AdminDrugDetail = lazy(() => import('./routes/admin-drug-detail'));
const AdminDoctors = lazy(() => import('./routes/admin-doctors'));

function App() {
  const { user, loading, profile, profileLoading } = useAuth();
  const location = useLocation();

  // PostHog receives the raw path by product decision; Sentry sanitizes the
  // same paths inside its own hooks.
  useEffect(() => {
    trackPageview(location.pathname);
  }, [location.pathname]);

  // Wait for auth *and* the first profile snapshot when signed in. Without
  // this, Home can briefly see needsOnboarding=true and replace the URL.
  if (loading || (user && profileLoading)) {
    return <AuthLoading />;
  }

  // A rejected PWZ ends access everywhere (the backend disabled the account and
  // revoked its tokens; this covers the rest of the current token's life).
  if (user && profile?.pwz_status === 'rejected') {
    return <AccountRejectedNotice />;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Suspense fallback={<AuthLoading />}>
        <Routes>
          <Route path="/" element={<Layout user={user} />}>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/invite" element={<Invite />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route
              path="/documents"
              element={
                <RequireOnboarded>
                  <Documents />
                </RequireOnboarded>
              }
            />
            <Route
              path="/feedback"
              element={
                <RequireOnboarded>
                  <Feedback />
                </RequireOnboarded>
              }
            />
            <Route
              path="/admin/drugs"
              element={
                <RequireOnboarded>
                  <AdminDrugs />
                </RequireOnboarded>
              }
            />
            <Route
              path="/admin/drugs/:drugId"
              element={
                <RequireOnboarded>
                  <AdminDrugDetail />
                </RequireOnboarded>
              }
            />
            <Route
              path="/admin/doctors"
              element={
                <RequireOnboarded>
                  <AdminDoctors />
                </RequireOnboarded>
              }
            />
            <Route path="/:query" element={<Home />} />
          </Route>
        </Routes>
      </Suspense>
    </TooltipProvider>
  );
}

export default App;
